import { useLocation, useNavigate } from 'react-router';

import {
  ClipboardPen,
  FileCheck2,
  FileSearch,
  FileText,
  Users,
} from 'lucide-react';

import { DEV_USERS, getUserInfo, setDevUser } from '@/lib/auth.js';
import { cn } from '@/lib/utils';

import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import { Select } from './ui/select';
import { Separator } from './ui/separator';

const AUTH_ENABLED = import.meta.env.VITE_ENABLE_AUTH === 'true';

const navList = [
  {
    match: ['/documents', '/document/'],
    text: 'Documents',
    to: '/documents',
  },
  {
    icon: FileSearch,
    match: ['/controls', '/control/'],
    text: 'Change Control',
    to: '/controls',
  },
  {
    icon: FileCheck2,
    match: ['/qualities', '/quality/'],
    text: 'Quality Event',
    to: '/qualities',
  },
  {
    icon: ClipboardPen,
    match: ['/audit-trail'],
    text: 'Audit Trail',
    to: '/audit-trail',
  },
];

const NavButton = ({
  icon: Icon = FileText,
  match,
  mobile,
  setMobileMenuOpen,
  sidebarCollapsed,
  text,
  to,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isMatch = match?.filter((item) => pathname.startsWith(item))?.length;

  const handleClick = () => {
    navigate(to);
    mobile && setMobileMenuOpen(false);
  };

  return (
    <Button
      className={cn(
        // w-full in BOTH states: the sidebar animates its own width, so the
        // button must not also snap between 100% and 2.5rem. Percentages and
        // rem don't interpolate, which is what made the old toggle jerk.
        'w-full transition-[padding,color,background-color] duration-200 ease-in-out',
        // p-0 lets the button's base justify-center take over.
        sidebarCollapsed && !mobile ? 'p-0' : 'px-4 justify-start'
      )}
      onClick={handleClick}
      variant={isMatch ? 'default' : 'ghost'}
    >
      <Icon className="h-4 w-4" />
      <span
        className={cn(
          // Wipes shut rather than unmounting, so the text no longer pops.
          'overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin] duration-200 ease-in-out',
          sidebarCollapsed && !mobile
            ? 'ml-0 max-w-0 opacity-0'
            : 'ml-1 max-w-[10rem] opacity-100'
        )}
      >
        {text}
      </span>
    </Button>
  );
};

const SidebarContent = ({
  mobile = false,
  sidebarCollapsed,
  setMobileMenuOpen,
}) => {
  const userInfo = getUserInfo();
  const displayName =
    userInfo?.name ||
    userInfo?.email ||
    userInfo?.['cognito:username'] ||
    'User';
  const initials = displayName.slice(0, 2).toUpperCase();
  const groups = userInfo?.['cognito:groups'] || [];
  const roleLabel = groups.length > 0 ? groups.join(', ') : '';

  return (
    <div className="space-y-4 py-4">
      <div className="px-3 py-2">
        {/* Kept mounted and animated rather than conditionally rendered, so the
            block collapses/expands instead of vanishing in one frame. */}
        <div className="space-y-1">
          {/* User Info */}
          <div
            className={cn(
              'flex items-center py-2 transition-[padding] duration-200 ease-in-out',
              // 4px left when collapsed so the avatar lands on the aside's
              // centre line, matching the nav icons below it. Padding is
              // animated rather than swapped for justify-center, which would
              // make the avatar wobble as the label wipes shut.
              sidebarCollapsed && !mobile ? 'pl-1 pr-2' : 'px-2'
            )}
          >
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div
              className={cn(
                'flex flex-col overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin] duration-200 ease-in-out',
                sidebarCollapsed && !mobile
                  ? 'ml-0 max-w-0 opacity-0'
                  : 'ml-3 max-w-[10rem] opacity-100'
              )}
            >
              <span className="text-sm font-medium">{displayName}</span>
              {roleLabel && (
                <span className="text-xs text-muted-foreground">
                  {roleLabel}
                </span>
              )}
            </div>
          </div>
          <div
            className={cn(
              'overflow-hidden transition-[max-height,opacity] duration-200 ease-in-out',
              sidebarCollapsed && !mobile
                ? 'max-h-0 opacity-0'
                : 'max-h-60 opacity-100'
            )}
          >
            <Separator />
            {!AUTH_ENABLED && (
              <div className="px-2 pt-1 pb-0">
                <p className="text-xs text-muted-foreground mb-1">
                  Switch User role
                </p>
                <Select
                  className="h-9 text-sm"
                  value={userInfo?.sub}
                  onChange={(e) => {
                    setDevUser(e.target.value);
                    window.location.reload();
                  }}
                >
                  {DEV_USERS.map((u) => (
                    <option key={u.sub} value={u.sub}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-3 py-2">
        {navList.map(({ icon, match, text, to }) => (
          <NavButton
            key={to}
            {...{
              match,
              mobile,
              setMobileMenuOpen,
              sidebarCollapsed,
              text,
              to,
              icon,
            }}
          />
        ))}
        {(groups.includes('PowerUser') || groups.includes('SuperAdmin')) && (
          <NavButton
            mobile={mobile}
            setMobileMenuOpen={setMobileMenuOpen}
            sidebarCollapsed={sidebarCollapsed}
            text="User Management"
            to="/admin/users"
            icon={Users}
          />
        )}
      </div>
    </div>
  );
};

export default SidebarContent;
